import React from 'react';

// Actual test images from Gegadyne - Before and After pairs
const testImages = {
  crush: {
    before: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/o66x0qfg_image.png",
    after: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/z92jhzj0_image.png",
  },
  shortCircuit: {
    before: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/oh4wknvv_image.png",
    after: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/oh4wknvv_image.png",
  },
  overcharge: {
    before: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/oh4wknvv_image.png",
    after: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/oh4wknvv_image.png",
  },
  thermalAbuse: {
    before: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/suou2egl_image.png",
    after: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/3xtrnl7p_image.png",
  },
  thermalShock: {
    before: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/1jd0lsxd_image.png",
    after: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/ia90p7kq_image.png",
  },
  drop: {
    before: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/oh4wknvv_image.png",
    after: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/oh4wknvv_image.png",
  },
  altitude: {
    before: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/2ekqn158_image.png",
    after: "https://customer-assets.emergentagent.com/job_gegadyne-energy/artifacts/cprl2u9d_image.png",
  },
};

const UN383ComplianceSection = () => {
  const tests = [
    {
      id: 'crush',
      name: 'Crush Test',
      result: 'No fire, no rupture',
      description: 'Cells subjected to 13 kN crushing force. Validates that physical damage during handling, transport accidents, or equipment collisions will not cause fire or hazardous material release.',
      beforeImage: testImages.crush.before,
      afterImage: testImages.crush.after,
    },
    {
      id: 'short-circuit',
      name: 'Short Circuit Test',
      result: 'Safe discharge observed',
      description: 'External short circuit applied at elevated temperature. Confirms that wiring faults or connector failures result in controlled discharge rather than thermal events.',
      beforeImage: testImages.shortCircuit.before,
      afterImage: testImages.shortCircuit.after,
    },
    {
      id: 'overcharge',
      name: 'Overcharge Test',
      result: 'Stable at 200% SOC',
      description: 'Charged to twice the rated capacity. Demonstrates that BMS failures or charger malfunctions do not compromise cell integrity—a critical safeguard for unattended charging.',
      beforeImage: testImages.overcharge.before,
      afterImage: testImages.overcharge.after,
    },
    {
      id: 'thermal-abuse',
      name: 'Thermal Abuse Test',
      result: 'Operable from -40°C to +75°C',
      description: 'Cells cycled through extreme temperature ranges for extended periods. Validates deployment in cold storage, foundries, and outdoor installations without performance degradation.',
      beforeImage: testImages.thermalAbuse.before,
      afterImage: testImages.thermalAbuse.after,
    },
    {
      id: 'thermal-shock',
      name: 'Thermal Shock Test',
      result: 'No thermal damage',
      description: 'Rapid temperature cycling between -40°C and +75°C. Ensures cells withstand sudden environmental changes—such as moving equipment from freezer to loading dock.',
      beforeImage: testImages.thermalShock.before,
      afterImage: testImages.thermalShock.after,
    },
    {
      id: 'drop',
      name: 'Drop Test',
      result: 'No structural failure',
      description: 'Cells dropped from 1.2m onto concrete surface. Confirms packaging and cell construction survive handling incidents during installation and maintenance.',
      beforeImage: testImages.drop.before,
      afterImage: testImages.drop.after,
    },
    {
      id: 'altitude',
      name: 'Altitude Test',
      result: 'Verified up to 49,000 ft',
      description: 'Tested at 11.6 kPa pressure (equivalent to 49,000 ft). Required for air freight certification—without this, batteries cannot be transported by cargo aircraft.',
      beforeImage: testImages.altitude.before,
      afterImage: testImages.altitude.after,
    },
  ];

  return (
    <section className="py-20 lg:py-24 bg-gray-950" data-testid="un383-compliance-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Transition */}
        <div className="text-center mb-8">
          <p className="text-gray-500 text-sm max-w-2xl mx-auto">
            Performance claims require verification. The following tests represent the regulatory standard for battery safety in transport and deployment.
          </p>
        </div>
        
        {/* Section Header - Dominant and Clear */}
        <header className="mb-16 text-center">
          <p className="text-gray-500 text-sm font-medium tracking-widest uppercase mb-4">
            Safety Certification
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-6">
            UN 38.3 Compliance
          </h2>
          <p className="text-gray-400 text-base max-w-2xl mx-auto leading-relaxed mb-4">
            UN 38.3 is the international standard governing the safe transport of lithium and non-lithium batteries by air, sea, and ground. Without this certification, batteries cannot legally be shipped as cargo.
          </p>
          <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">
            All Gegadyne Multi-Ion cells and packs have passed the complete UN 38.3 test sequence at NABL-accredited facilities. Test evidence and certification documents are available for procurement review.
          </p>
        </header>

        {/* Test Results - Clean Vertical List with Images */}
        <div className="space-y-0 border-t border-gray-800">
          {tests.map((test, index) => (
            <div 
              key={test.id}
              className="border-b border-gray-800 py-8"
              data-testid={`test-row-${test.id}`}
            >
              <div className="grid lg:grid-cols-12 gap-6 items-start">
                
                {/* Test Info - Primary Focus */}
                <div className="lg:col-span-5">
                  <div className="flex items-start gap-4">
                    <span className="text-gray-700 text-sm font-mono">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="text-white text-lg font-medium mb-2">
                        {test.name}
                      </h3>
                      <p className="text-emerald-400 text-sm font-medium mb-2">
                        {test.result}
                      </p>
                      <p className="text-gray-500 text-sm leading-relaxed">
                        {test.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Before/After Images - Evidence, Not Decoration */}
                <div className="lg:col-span-6">
                  <div className="flex gap-4">
                    {/* Before */}
                    <div className="flex-1">
                      <p className="text-gray-600 text-xs uppercase tracking-wide mb-2">Before</p>
                      <div className="aspect-[4/3] bg-gray-900 rounded overflow-hidden">
                        <img 
                          src={test.beforeImage}
                          alt={`${test.name} - Before test`}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    {/* After */}
                    <div className="flex-1">
                      <p className="text-gray-600 text-xs uppercase tracking-wide mb-2">After</p>
                      <div className="aspect-[4/3] bg-gray-900 rounded overflow-hidden">
                        <img 
                          src={test.afterImage}
                          alt={`${test.name} - After test`}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status - Simple Text */}
                <div className="lg:col-span-1 lg:text-right">
                  <span className="text-emerald-500 text-sm font-medium">
                    Pass
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-gray-800 text-center">
          <p className="text-gray-500 text-sm mb-4">
            Complete UN 38.3 test reports and certification documents available upon request.
          </p>
          <a 
            href="#contact"
            className="text-emerald-500 hover:text-emerald-400 text-sm font-medium transition-colors"
            data-testid="request-documentation-link"
          >
            Request Documentation →
          </a>
        </footer>
      </div>
    </section>
  );
};

export default UN383ComplianceSection;
